import React from 'react';
import themes from '../contentBoxThemes.json';

const DEFAULT_BOX_CLASS =
    "bg-offwhite border border-gray-300 shadow-[0_3px_0_0_rgba(0,0,0,0.5)]";

const DEFAULT_HEADER_TEXT_CLASS =
    "text-white tracking-wider font-['Jersey_10'] uppercase text-[18px] tracking-[1px] py-2 [-webkit-font-smoothing:none] [-moz-osx-font-smoothing:grayscale] [text-rendering:optimizeSpeed]";

const DEFAULT_HEADER_TEXT_SHADOW =
    "-1px 0 0 #000, 1px 0 0 #000, 0 -1px 0 #000, 0 1px 0 #000, 0px 0px 50px rgba(0,0,0,1), 0px 0px 50px rgba(0,0,0,1)";

function toRGBA(value) {
    if (value.startsWith("#")) {
        return `${value}80`;
    }
    const c = document.createElement("canvas").getContext("2d");
    c.fillStyle = value;
    const hex = c.fillStyle;
    return hex.length === 7 ? `${hex}80` : hex;
}

function ContentBox({ theme = "default", header = "", icon, tint, headerBackground, headerBackgroundMode, headerBackgroundVerticalOffset, headerRight, padding = "p-4", className = "", contentClassName = "", children }) {
    const t = themes[theme] || themes["default"];

    const resolvedHeaderBackground = headerBackground || t.headerBackground;
    const isRawCss = resolvedHeaderBackground && !resolvedHeaderBackground.startsWith('/') && !resolvedHeaderBackground.startsWith('http') && !resolvedHeaderBackground.startsWith('data:');

    const hasVOffset = !!headerBackgroundVerticalOffset && !!resolvedHeaderBackground && !isRawCss;

    const preset = t.presetHeader || "";
    const finalHeader = preset !== "" ? preset : header;

    const isOgGold = theme === "OG-gold";

    const iconSrc = icon !== undefined ? icon : (t.headerImage || null);
    const hasIcon = !!iconSrc;
    const isGif = iconSrc && iconSrc.endsWith('.gif');

    if (isOgGold) {
        return (
            <div className={`bg-offwhite border border-gray-300 ${padding} mb-4 shadow-[0_3px_0_0_rgba(0,0,0,0.5)]`}>
                {(finalHeader || hasIcon) && (
                    <div className="flex items-center gap-2 mb-2">
                        {hasIcon && (
                            <img
                                src={`${process.env.PUBLIC_URL}${iconSrc}`}
                                alt=""
                                className={isGif ? 'mb-1' : ''}
                            />
                        )}

                        {finalHeader && (
                            <h2 className="text-maroon uppercase font-bold">
                                {finalHeader}
                            </h2>
                        )}
                    </div>
                )}

                <hr className="border-gold mb-3" />

                {children}
            </div>
        );
    }

    const headerTint = tint || t.headerBackgroundTint;

    const gradientTint = t.headerGradientTint || null;

    const bgImageValue = resolvedHeaderBackground
        ? (gradientTint
            ? `${gradientTint}, ${isRawCss ? resolvedHeaderBackground : `url(${resolvedHeaderBackground})`}`
            : (isRawCss ? resolvedHeaderBackground : `url(${resolvedHeaderBackground})`))
        : undefined;

    const headerStyle = {
        ...(resolvedHeaderBackground && {
            backgroundImage: bgImageValue,
            backgroundRepeat: gradientTint
                ? `no-repeat, ${t.headerBackgroundRepeat || "no-repeat"}`
                : (t.headerBackgroundRepeat || "no-repeat"),
            backgroundSize: gradientTint
                ? `100% 100%, ${t.headerBackgroundSize || "cover"}`
                : (t.headerBackgroundSize || "cover"),
            backgroundPosition: t.headerBackgroundPosition || "left top",
            overflow: "hidden",
        }),
        ...(headerTint && {
            backgroundColor: headerBackground ? headerTint : toRGBA(headerTint),
            ...(headerBackground && headerBackgroundMode !== 'overlay' && { backgroundBlendMode: "multiply" }),
            ...(!headerBackground && { backgroundBlendMode: "screen" }),
        }),
        ...(hasIcon ? { paddingLeft: '1.2rem' } : {}),
    };

    const titleStyle = {
        textShadow: DEFAULT_HEADER_TEXT_SHADOW
    };


    return (
        <div className={`${DEFAULT_BOX_CLASS} mb-4 ${className}`}>
            {(finalHeader || hasIcon || headerRight) && (
                <div
                    className="flex items-center gap-2 px-4"
                    style={hasVOffset ? { ...headerStyle, backgroundImage: undefined, position: 'relative', overflow: 'hidden' } : headerStyle}
                >
                    {hasVOffset && (
                        <div aria-hidden="true" className="absolute inset-0 flex items-stretch overflow-hidden" style={{ zIndex: 0 }}>
                            {Array.from({ length: 30 }).map((_, i) => (
                                <div
                                    key={i}
                                    style={{
                                        flexShrink: 0,
                                        backgroundImage: `url(${resolvedHeaderBackground})`,
                                        backgroundRepeat: 'repeat-y',
                                        backgroundSize: 'auto',
                                        backgroundPositionY: `${i * headerBackgroundVerticalOffset}px`,
                                    }}
                                >
                                    <img src={resolvedHeaderBackground} alt="" draggable={false} style={{ visibility: 'hidden', display: 'block' }} />
                                </div>
                            ))}
                        </div>
                    )}

                    {hasIcon && (
                        <img
                            src={`${process.env.PUBLIC_URL}${iconSrc}`}
                            alt=""
                            className={isGif ? 'mb-1' : ''}
                            style={hasVOffset ? { position: 'relative', zIndex: 1 } : undefined}
                        />
                    )}

                    {finalHeader && (
                        <h2 className={DEFAULT_HEADER_TEXT_CLASS} style={hasVOffset ? { ...titleStyle, position: 'relative', zIndex: 1 } : titleStyle}>
                            {finalHeader}
                        </h2>
                    )}

                    {headerRight && (
                        <div className="ml-auto flex shrink-0 items-center gap-2" style={{ position: 'relative', zIndex: 1 }}>
                            {headerRight}
                        </div>
                    )}
                </div>
            )}

            <div className={`${padding} ${contentClassName}`}>
                {children}
            </div>
        </div>
    );
}

export default ContentBox;